/**
 * Contentful Images API helpers.
 *
 * Contentful serves the ORIGINAL upload from images.ctfassets.net when you use
 * `file.url` verbatim — full resolution, original format (usually JPEG/PNG).
 * Dropping a 2000px+ original into a 220–390px slot ships megabytes the layout
 * never uses, which is a direct LCP/data-usage hit on mobile. The Images API
 * resizes/reformats on the fly via query params, so we ask for exactly the slot
 * size in WebP: https://www.contentful.com/developers/docs/references/images-api/
 *
 * Host-guarded on purpose: hero art and icons are SVGs on the R2 CDN
 * (assets.askhippo.co.za), which ignores these params. So anything that isn't an
 * images.ctfassets.net URL is returned unchanged — these helpers are always safe
 * to call regardless of where the image is hosted.
 */

const CONTENTFUL_IMAGE_HOST = 'images.ctfassets.net';

// Contentful `file.url` values are protocol-relative (//images.ctfassets.net/…);
// some call sites already prepend `https:` and some don't. Normalise both.
const normalizeUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  return trimmed;
};

const isContentfulAsset = (url) => {
  try {
    return new URL(url).hostname === CONTENTFUL_IMAGE_HOST;
  } catch {
    return false;
  }
};

/**
 * Build a resized/reformatted Contentful image URL for a given slot.
 * Non-Contentful URLs (R2 SVGs, external images) are returned normalised but
 * untransformed.
 *
 * @param {string} url
 * @param {object} [opts]
 * @param {number} [opts.width]   intrinsic pixel width to request
 * @param {number} [opts.height]  intrinsic pixel height to request
 * @param {'fill'|'pad'|'crop'|'scale'|'thumb'} [opts.fit='fill'] applied only when both w & h are set
 * @param {number} [opts.quality=70]
 * @param {'webp'|'jpg'|'png'|'avif'} [opts.format='webp']
 */
export const getContentfulImageUrl = (
  url,
  { width, height, fit = 'fill', quality = 70, format = 'webp' } = {}
) => {
  const normalized = normalizeUrl(url);
  if (!isContentfulAsset(normalized)) return normalized;

  const parsed = new URL(normalized);
  if (width) parsed.searchParams.set('w', String(Math.round(width)));
  if (height) parsed.searchParams.set('h', String(Math.round(height)));
  if (width && height) parsed.searchParams.set('fit', fit);
  if (format) parsed.searchParams.set('fm', format);
  if (quality) parsed.searchParams.set('q', String(quality));
  return parsed.toString();
};

/**
 * Build a 1x/2x srcset for a Contentful image slot so retina screens get a
 * crisp image without every device paying for 2x. Returns '' for non-Contentful
 * URLs (or when no width is given), so callers can spread it conditionally.
 */
export const getContentfulSrcSet = (
  url,
  { width, height, fit, quality, format } = {}
) => {
  const normalized = normalizeUrl(url);
  if (!isContentfulAsset(normalized) || !width) return '';

  return [1, 2]
    .map((dpr) => {
      const scaledUrl = getContentfulImageUrl(normalized, {
        width: width * dpr,
        height: height ? height * dpr : undefined,
        fit,
        quality,
        format,
      });
      return `${scaledUrl} ${dpr}x`;
    })
    .join(', ');
};
